/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

// @mui material components
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";

// Material Dashboard 2 PRO React components
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Service
import * as Defi from "services/Define";
import * as Func from "services/Function";
import { FetchData } from "services/Request";
import TaskMenu from "./TaskMenu";

const stepperName = {
  edit: {
    name: "รอนำเสนอ",
    color: "warning",
    icon: "border_color",
  },
  present: {
    name: "นำเสนอระบบ",
    color: "info",
    icon: "emoji_people",
  },
  quotation: {
    name: "เสนอราคา",
    color: "info",
    icon: "description",
  },
  approve: {
    name: "อนุมัติสั่งซื้อ",
    color: "success",
    icon: "task_alt",
  },
  develop: {
    name: "พัฒนาระบบ",
    color: "primary",
    icon: "computer",
  },
  implement: {
    name: "ติดตั้งระบบ",
    color: "success",
    icon: "settings_suggest",
  },
  success: {
    name: "ใช้งานระบบ",
    color: "success",
    icon: "check_circle",
  },
  reject: {
    name: "ลูกค้าปฏิเสธ",
    color: "error",
    icon: "cancel",
  },
};

export default function Task() {
  const mod = "task";
  const app = useSelector((state) => state[Defi.APP_STATE]);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleClose = () => {
    setOpen(false);
  };

  const view = () => {
    setLoading(true);
    setList([]);

    FetchData("POST", `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === "ok") {
        setList(result.data);
        setOpen(true);
        setLoading(false);
      } else {
        Func.exitApp();
      }
    });
  };

  const show = (task_id) => {
    setList([]);

    FetchData("OPTIONS", `/${mod}/open`, app.token, {
      lang: app.lang,
      id: task_id,
    }).then((result) => {
      if (result.status === "ok") {
        // setList(result.data);
        // setOpen(true);
      } else {
        Func.exitApp();
      }
    });
  };

  const approve = (task_id, status) => {
    setList([]);

    FetchData("OPTIONS", `/${mod}/approve`, app.token, {
      lang: app.lang,
      id: task_id,
      status: status,
    }).then((result) => {
      if (result.status === "ok") {
        view();
      } else {
        Func.exitApp();
      }
    });
  };

  const checkMenu = (task_id, action) => {
    switch (action) {
      case "detail":
        show(task_id);
        break;
      case "present":
      case "quotation":
      case "implement":
      case "success":
      case "reject":
        approve(task_id, action);
        break;
      default:
        break;
    }
  };

  const projectTypeDisplay = (input) => {
    if (input === "SUB") return "ค่าบริการรายปี";
    if (input === "BUY") return "ซื้อขาด";
    return "";
  };

  const renderLoading = () => {
    return (
      <TableContainer sx={{ boxShadow: 0 }}>
        <Table aria-label="simple table" size="small">
          <TableBody>
            <TableRow>
              <TableCell colSpan={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Skeleton animation="wave" height={10} width="100%" />
                  </Grid>
                  <Grid item xs={3}>
                    <Skeleton animation="wave" height={10} width="90%" />
                  </Grid>
                  <Grid item xs={9}>
                    <Skeleton animation="wave" height={10} width="100%" />
                  </Grid>
                  <Grid item xs={3}>
                    &nbsp;
                  </Grid>
                  <Grid item xs={9}>
                    <Skeleton animation="wave" height={10} width="100%" />
                  </Grid>
                </Grid>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderDialog = () => {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullScreen
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
              <Icon>arrow_back_ios</Icon>
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              ติดตามลูกค้า
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent dividers sx={{ p: 0 }}>
          {loading ? (
            renderLoading()
          ) : (
            <TableContainer sx={{ boxShadow: 0 }}>
              <Table aria-label="simple table" size="small">
                <TableBody>
                  {list.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell colSpan={2}>
                        <Grid container>
                          <Grid item xs={10}>
                            <MDTypography variant="h6">{item.customer_name}</MDTypography>
                          </Grid>
                          <Grid item xs={2} className="text-right">
                            <TaskMenu parent={item} callFunction={approve} />
                          </Grid>
                          <Grid item xs={4}>
                            <Chip
                              size="small"
                              label={stepperName[item.status].name}
                              color={stepperName[item.status].color}
                            />
                          </Grid>
                          <Grid item xs={3} className="text-right">
                            <MDTypography variant="body">Total :</MDTypography>
                          </Grid>
                          <Grid item xs={5} className="text-right">
                            {item.total}
                          </Grid>
                          <Grid item xs={4}>
                            <MDTypography variant="caption">
                              {Func.dateTimeDisplay(item.updated_at)}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={3} className="text-right">
                            <MDTypography variant="body">Vat :</MDTypography>
                          </Grid>
                          <Grid item xs={5} className="text-right">
                            {item.vat}
                          </Grid>
                          <Grid item xs={7} className="text-right">
                            <MDTypography variant="body">Net Total :</MDTypography>
                          </Grid>
                          <Grid item xs={5} className="text-right">
                            {item.nettotal}
                          </Grid>
                          <Grid item xs={12}>
                            <MDTypography variant="body">
                              ประเภท : {projectTypeDisplay(item.project_type)}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={12}>
                            <MDTypography variant="body">หมายเหตุ : {item.remark}</MDTypography>
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div>
      <MDButton variant="text" color="light" size="large" onClick={view}>
        <Icon>account_circle</Icon>
      </MDButton>
      <MDButton variant="text" color="light" size="large" onClick={view}>
        <Icon>settings_input_composite</Icon>
      </MDButton>
      {renderDialog()}
    </div>
  );
}
